import { ReportSettings } from "@/inspection/shared/dtos/ReportSettings";
import { InspectionRepoContainer } from "@/inspection/shared/repositories/InspectionRepoContainer";
import { DynamicForm } from "@/shared/form/DynamicForm";
import { RequestModel } from "@/shared/model/RequestModel";
import { RequestHandler } from "@/shared/utils/RequestHandler";
import { UserSettings } from "@/storage/UserSettings";
import { TypeChecker } from "@/shared/utils/TypeChecker";
import { Report } from "@/inspection/shared/models/Report";
import { AnyForm, reportFormatDefinition } from "./ReportFormatDefinitions";

export class ReportSettingsModel extends RequestModel<ReportSettings> {
  public report = new Report();

  private form: AnyForm = new DynamicForm({});
  private assignmentId = 0;
  private reportType = "";
  private reportId = 0;

  public get settingsForm() {
    return this.form;
  }

  public get hasTicketSelection() {
    return reportFormatDefinition(this.reportType)?.hasTicketSelection === true;
  }

  public load(assignmentId: string, reportType: string, reportId: string) {
    this.assignmentId = parseInt(assignmentId, 10);
    this.reportType = reportType;
    this.reportId = parseInt(reportId, 10);

    const wiring = this.buildForm();

    RequestHandler.handleModel(
      async () => {
        // the form creates its fields asynchronously; awaiting that here
        // guarantees that from() writes into the finished fields
        await wiring;

        return InspectionRepoContainer.reportRepo.getReportSettings(
          UserSettings.getNumericUserId(),
          assignmentId,
          reportType
        );
      },
      this
    );
  }

  public loadReport() {
    if (!this.assignmentId || !this.reportType) {
      return;
    }

    this.report.init({
      assignmentId: this.assignmentId,
      reportType: this.reportType
    });
    this.report.load();
  }

  public getFormField(fieldname: string) {
    return this.form.fieldExists(fieldname)
      ? this.form.getField(fieldname)
      : undefined;
  }

  public setFormField(fieldName: string, value: unknown) {
    RequestHandler.handleFormUpdate(
      () => {
        const settings = JSON.stringify(this.form.getData());
        return InspectionRepoContainer.reportRepo.updateReportSettings(
          settings,
          this.assignmentId,
          this.reportType,
          this.reportId
        );
      },
      {
        form: this.form,
        fieldName,
        value
      }
    );
  }

  public from(data: ReportSettings): void {
    for (const key of Object.keys(data)) {
      const value = data[key];

      if (this.form.fieldExists(key)) {
        if (key === "priorityFilter" && TypeChecker.isString(value)) {
          this.form.setFieldValue(key, [value]);
        } else if (key === "hidden_ticket_ids") {
          this.form.setFieldValue(key, TypeChecker.isArray(value) ? value : []);
        } else {
          this.form.setFieldValue(key, value);
        }
      }
    }
  }

  private buildForm(): Promise<unknown> {
    const definition = reportFormatDefinition(this.reportType);

    this.form = definition?.createSettingsForm?.() ?? new DynamicForm({});

    return this.form.init();
  }
}
